/* eslint-disable react/prop-types */
import React from "react";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { ThreeDots } from "react-loader-spinner";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import Tooltip from "@material-ui/core/Tooltip";
import Search from "@material-ui/icons/Search";
import FilterNone from "@material-ui/icons/FilterNone";
import CheckBox from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlank from "@material-ui/icons/CheckBoxOutlineBlank";
import { grayColor } from "../../../assets/jss/material-dashboard-pro-react";
import Button from "../../../components/CustomButtons/Button";
import ModalHeader from "../../../components/ModalHeader";

export default function InputNotesDialog({
  open,
  onClose,
  input,
  verifyingNoteUuids,
  onMarkAsAnalyzed,
  onValuesCopied,
  classes,
  Transition,
}) {
  const { t, i18n } = useTranslation();

  if (!input) return null;

  return (
    <Dialog
      classes={{
        root: classes.modalRoot,
        paper: classes.modal,
      }}
      open={open}
      TransitionComponent={Transition}
      onClose={onClose}
      aria-labelledby="classic-modal-slide-title"
      aria-describedby="classic-modal-slide-description"
      fullWidth
      maxWidth={"md"}
    >
      <ModalHeader
        id="content-modal-slide-title"
        title={input.input_name[i18n.language]}
        subtitle={`${t("modeling_inputs_v2.modals.display_notes.input_notes")} (${t(
          "modeling_inputs_v2.modals.display_notes.system_generated",
        )})`}
        pills={[
          {
            key: "notes",
            label: t("modeling_inputs_v2.modals.display_notes.notes_count", {
              count: input.notes.length,
            }),
          },
        ]}
        onClose={onClose}
        closeLabel={t("common.close")}
      />
      <DialogContent
        id="classic-modal-slide-description"
        className={classes.modalBody}
        style={{ paddingLeft: "2px", paddingRight: "2px" }}
      >
        <div className={classes.displayNotesTable}>
          <div
            className={cx({
              [classes.displayNotesTableHeader]: true,
              [classes.displayNotesTableRow]: true,
            })}
          >
            <div className={classes.displayNotesTableColCheckStage}>
              {t("modeling_inputs_v2.modals.display_notes.check_stage")}
            </div>
            <div className={classes.displayNotesTableColSeverity}>
              {t("modeling_inputs_v2.modals.display_notes.severity")}
            </div>
            <div className={classes.displayNotesTableColDescription}>
              {t("modeling_inputs_v2.modals.display_notes.description")}
            </div>
            <div className={classes.displayNotesTableColComments}>
              {t("modeling_inputs_v2.modals.display_notes.comments")}
            </div>
            <div className={classes.displayNotesTableColCopyValues}>
              {t("modeling_inputs_v2.modals.display_notes.copy_values")}
            </div>
            <div className={classes.displayNotesTableColMarkAsAnalyzed}>
              {t("modeling_inputs_v2.modals.display_notes.mark_as_analyzed")}
            </div>
          </div>
          <div className={classes.displayNotesTableContent}>
            {input.notes.map((note, key) => {
              return (
                <div key={key} className={classes.displayNotesTableRow}>
                  <div className={classes.displayNotesTableColCheckStage}>
                    {t("modeling_inputs_v2.stages." + note.type)}
                  </div>
                  <div
                    className={cx({
                      [classes.displayNotesTableColSeverity]: true,
                      ["severity" + note.severity]: true,
                    })}
                  >
                    {t(
                      "modeling_inputs_v2.modals.display_notes.severity" +
                        note.severity,
                    )}
                  </div>
                  <div className={classes.displayNotesTableColDescription}>
                    <Tooltip title={note.description[i18n.language]}>
                      <Search className={classes.displayNotesTableIcon} />
                    </Tooltip>
                  </div>
                  <div className={classes.displayNotesTableColComments}>
                    {note.notes[i18n.language]}
                  </div>
                  <div className={classes.displayNotesTableColCopyValues}>
                    {note.values && (
                      <CopyToClipboard
                        text={note.values}
                        onCopy={onValuesCopied}
                      >
                        <Tooltip
                          title={t(
                            "modeling_inputs_v2.modals.display_notes.copy_related_values",
                          )}
                        >
                          <FilterNone
                            className={classes.displayNotesTableIcon}
                          />
                        </Tooltip>
                      </CopyToClipboard>
                    )}
                  </div>
                  <div className={classes.displayNotesTableColMarkAsAnalyzed}>
                    {!note.marked_as_verified &&
                      !verifyingNoteUuids.includes(note.uuid) && (
                        <Tooltip
                          title={t(
                            "modeling_inputs_v2.modals.display_notes.mark_as_analyzed_note",
                          )}
                        >
                          <CheckBoxOutlineBlank
                            className={cx({
                              [classes.displayNotesTableIcon]: true,
                              [classes.displayNotesTableMarkAsAnalyzedIcon]: true,
                            })}
                            onClick={() => onMarkAsAnalyzed(note.uuid)}
                          />
                        </Tooltip>
                      )}
                    {!note.marked_as_verified &&
                      verifyingNoteUuids.includes(note.uuid) && (
                        <div className={classes.displayNotesTableSpinner}>
                          <ThreeDots
                            height="18px"
                            width="18px"
                            color={grayColor[1]}
                          />
                        </div>
                      )}
                    {note.marked_as_verified && (
                      <Tooltip
                        title={`${t("modeling_inputs_v2.modals.display_notes.mark_as_analyzed_by")} ${note.marked_as_verified_by}`}
                      >
                        <CheckBox
                          className={cx({
                            [classes.displayNotesTableIcon]: true,
                            [classes.displayNotesTableMarkAsAnalyzedIcon]: true,
                            [classes.displayNotesTableMarkAsAnalyzedDoneIcon]: true,
                          })}
                        />
                      </Tooltip>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </DialogContent>
      <DialogActions className={classes.modalFooter}>
        <Button onClick={onClose} color="danger" simple>
          {t("modeling_inputs_v2.modals.display_notes.close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
